import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import JsonLd, { breadcrumb, floorPlanSchema } from "@/components/JsonLd";
import { Ruler, BedDouble, Bath, Car, ArrowRight, Building2 } from "lucide-react";

export default function FloorPlans() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/floor-plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  return (
    <PublicLayout>
      <JsonLd id="bc-plans" data={breadcrumb([{name: "Home", url: "/"}, {name: "Floor Plans", url: "/floor-plans"}])} />
      {plans.map((p) => <JsonLd key={p.id} id={`plan-${p.id}`} data={floorPlanSchema(p)} />)}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
        <div className="label-overline mb-4">For new construction buyers</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
          Pick a floor plan. <span style={{color:'var(--accent)'}}>We&rsquo;ll build it on your lot.</span>
        </h1>
        <p className="text-lg mt-6 max-w-2xl leading-relaxed" style={{color:'var(--text-muted)'}}>
          Pre-engineered, Coastal Bend–ready home plans. Customize finishes, add a story, or work with us on something fully custom.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map((p) => (
          <Link to={`/floor-plans/${p.id}`} key={p.id} className="card overflow-hidden group" data-testid={`plan-card-${p.id}`}>
            <div className="aspect-[16/10] overflow-hidden">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-6">
              <div className="flex items-start justify-between mb-2 gap-3">
                <h3 className="font-display text-2xl font-bold">{p.name}</h3>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest" style={{color:'var(--text-muted)'}}>From</div>
                  <div className="font-display text-lg font-bold" style={{color:'var(--accent)'}}>{money(p.price_from)}</div>
                </div>
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{color:'var(--text-muted)'}}>{p.description}</p>
              <div className="grid grid-cols-4 gap-2 pt-4 border-t border-white/5">
                <Stat icon={Ruler} label={`${p.sqft.toLocaleString()} sqft`} />
                <Stat icon={BedDouble} label={`${p.bedrooms} bed`} />
                <Stat icon={Bath} label={`${p.bathrooms} bath`} />
                <Stat icon={Car} label={`${p.garage}-car`} />
              </div>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium" style={{color:'var(--accent)'}}>
                Explore plan <ArrowRight size={14} className="transition-transform group-hover:translate-x-1"/>
              </div>
            </div>
          </Link>
        ))}
        {plans.length === 0 && (
          <div className="md:col-span-2 card p-12 text-center" style={{color:'var(--text-muted)'}}>
            <Building2 className="mx-auto mb-3" />
            Floor plans are loading…
          </div>
        )}
      </section>
    </PublicLayout>
  );
}

function Stat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs" style={{color:'var(--text-muted)'}}>
      <Icon size={13} style={{color:'var(--accent)'}}/> {label}
    </div>
  );
}
